// Sections + cards store for the questionnaire editor.
//
// Reads (sections, cards) now come from the real backend via
// apiService.fetchSectionsForQuestionnaire(slug) and
// apiService.fetchCardsForQuestionnaire(slug). The detail page calls
// loadQuestionnaireData(slug, ...) on mount to seed this store with
// whatever the API returns.
//
// Writes (add/delete/reorder, including from the inline mini-forms) are
// still local-only. They mutate cardsState but do not call the backend
// yet — the next iteration adds POST/DELETE/PATCH wiring. Until then,
// any local edit is reverted by a page reload because the next mount
// re-fetches from the API.
//
// Schema reference: seq_card_sections + seq_cards in
// database/schema/seq_database_schema_sec_cards.md.
//
// Endpoint shapes:
//
//   GET    /api/seq/questionnaires/:slug/sections   ← WIRED
//   GET    /api/seq/questionnaires/:slug/cards      ← WIRED
//   POST   /api/seq/questionnaires/:slug/sections   (next iteration)
//   POST   /api/seq/sections/:id/cards              (next iteration)
//   DELETE /api/seq/sections/:id                    (next iteration)
//   DELETE /api/seq/cards/:id                       (next iteration)
//   PATCH  /api/seq/questionnaires/:slug/sections/order  (next iteration)
//   PATCH  /api/seq/sections/:id/cards/order        (next iteration)
//
// Names in seq_card_sections.name are JSONB (multilingual: {de, en, fr, it}).
// The UI shows DE first, falling back to EN. Codes in seq_cards.code are
// uppercase per the schema CHECK (^[A-Z][A-Z0-9_]{0,31}$) and stable
// across reorders.
//
// `questionnaire_slug` is a denormalised field stamped onto every row in
// the local store so the read helpers can scope by slug without a join.
// The backend doesn't expose it (sections/cards reference questionnaire
// by id) — loadQuestionnaireData adds it client-side.

import { reactive } from 'vue';

// Local-add id counters. Bumped by loadQuestionnaireData to stay above
// any backend-assigned id we've seen, so an "Add card" via the inline
// form can't collide with an existing row's id during the local-only
// phase. Phase 2 (real POST endpoints) replaces this with whatever id
// the backend hands back.
let nextSectionId = 100000;
let nextCardId = 100000;


export const cardsState = reactive({
  sections: [],
  // Legacy collection — populated by loadQuestionnaireData() from the
  // /api/seq/questionnaires/:slug/cards endpoint. DORMANT after the
  // junction Step 2 cutover: the questionnaire detail page now renders
  // from `items` below, not from this array. Kept in the store so the
  // old mutation helpers (addCard / updateCard / removeCard / etc.)
  // still resolve at import time; they are not called by the rendered
  // UI. Removed in a later cleanup pass once Step 3 is verified.
  cards: [],
  // Active collection — populated by loadQuestionnaireItems() from the
  // /api/seq/questionnaires/:slug/items endpoint. Each entry has shape:
  //   { id, questionnaire_id, section_id, section_name, section_slug,
  //     position, item_type, item: { ... inlined source row ... } }
  // The detail page reads from this. Writes (add/remove/reorder) come
  // in junction Step 3.
  items: [],
});

// Replace whatever the store has for `questionnaireSlug` with fresh data
// from the API. Other questionnaires' rows are untouched. Stamps the
// slug onto every row so the slug-keyed read helpers continue to work
// (the API response doesn't include that field).
//
// Bumps the local-add id counters past anything we've seen so a
// subsequent inline-form add can't collide with an existing id during
// the local-only edit phase.
export function loadQuestionnaireData(questionnaireSlug, sections, cards) {
  // Drop existing entries for this slug. Iterate in reverse so splice
  // doesn't disturb the indices of items we haven't checked yet.
  for (let i = cardsState.sections.length - 1; i >= 0; i--) {
    if (cardsState.sections[i].questionnaire_slug === questionnaireSlug) {
      cardsState.sections.splice(i, 1);
    }
  }
  for (let i = cardsState.cards.length - 1; i >= 0; i--) {
    if (cardsState.cards[i].questionnaire_slug === questionnaireSlug) {
      cardsState.cards.splice(i, 1);
    }
  }

  // Push fresh data with the slug stamp.
  for (const s of (sections || [])) {
    cardsState.sections.push({ ...s, questionnaire_slug: questionnaireSlug });
    if (typeof s.id === 'number' && s.id >= nextSectionId) {
      nextSectionId = s.id + 1;
    }
  }
  for (const c of (cards || [])) {
    cardsState.cards.push({ ...c, questionnaire_slug: questionnaireSlug });
    if (typeof c.id === 'number' && c.id >= nextCardId) {
      nextCardId = c.id + 1;
    }
  }
}

// Sections-only counterpart to loadQuestionnaireData. Replaces the
// sections slice for one questionnaire without touching cardsState.cards
// (which is dormant post-junction-Step-2). Used together with
// loadQuestionnaireItems by the detail page.
export function loadQuestionnaireSections(questionnaireSlug, sections) {
  for (let i = cardsState.sections.length - 1; i >= 0; i--) {
    if (cardsState.sections[i].questionnaire_slug === questionnaireSlug) {
      cardsState.sections.splice(i, 1);
    }
  }
  for (const s of (sections || [])) {
    cardsState.sections.push({ ...s, questionnaire_slug: questionnaireSlug });
    if (typeof s.id === 'number' && s.id >= nextSectionId) {
      nextSectionId = s.id + 1;
    }
  }
}

// Replace the items[] slice for one questionnaire with whatever the
// /items endpoint returned. Stamps `questionnaire_slug` onto every row
// so the slug-keyed read helper below can filter without a join — the
// API response doesn't include it. Backend ordering is preserved by
// pushing in order.
export function loadQuestionnaireItems(questionnaireSlug, items) {
  for (let i = cardsState.items.length - 1; i >= 0; i--) {
    if (cardsState.items[i].questionnaire_slug === questionnaireSlug) {
      cardsState.items.splice(i, 1);
    }
  }
  for (const it of (items || [])) {
    cardsState.items.push({ ...it, questionnaire_slug: questionnaireSlug });
  }
}

// ---------- read helpers (slug-keyed) ----------

export function sectionsForQuestionnaire(questionnaireSlug) {
  return cardsState.sections
    .filter(s => s.questionnaire_slug === questionnaireSlug)
    .slice()
    .sort((a, b) => a.position - b.position);
}

// Items belonging to this questionnaire, preserving the order in which
// the backend returned them (already null-section-first, then by
// section.position, then by item.position).
export function itemsForQuestionnaire(questionnaireSlug) {
  return cardsState.items
    .filter(it => it.questionnaire_slug === questionnaireSlug);
}

// Items under a specific section in this questionnaire, in their stored
// order. For null sections, pass null. Used by QuestionnaireSectionList
// to do the left-join render: walk all sections, attach matching items.
export function itemsForSection(questionnaireSlug, sectionId) {
  return cardsState.items
    .filter(it =>
      it.questionnaire_slug === questionnaireSlug
      && it.section_id === sectionId
    );
}

// ---- Step 3 (write path) local-state helpers --------------------------

// Append a freshly-created junction row to cardsState.items. The API
// response carries the resolved-item shape but NOT the questionnaire_slug
// (the backend keys on id) — stamp it client-side so the slug-keyed
// read helpers above continue to find this item without a refetch.
export function appendItemToCardsState(questionnaireSlug, newItem) {
  if (!newItem) return;
  cardsState.items.push({ ...newItem, questionnaire_slug: questionnaireSlug });
}

// Remove a junction row by id. Returns the removed row (so callers can
// optimistically revert by re-pushing it if the API call fails).
export function removeItemFromCardsState(itemId) {
  const idx = cardsState.items.findIndex(it => it.id === itemId);
  if (idx === -1) return null;
  const removed = cardsState.items[idx];
  cardsState.items.splice(idx, 1);
  return removed;
}

// Apply a new order to one section's items. Mirrors the backend's
// stride-10 (10, 20, 30, ...) so a refresh-without-fetch sees the
// same positions as a fresh fetch. Returns a snapshot of the prior
// (item id → position) mapping so callers can revert on API failure.
export function reorderItemsLocally(questionnaireSlug, sectionId, orderedIds) {
  const prev = new Map();
  for (const it of cardsState.items) {
    if (it.questionnaire_slug !== questionnaireSlug) continue;
    if (it.section_id !== sectionId) continue;
    prev.set(it.id, it.position);
  }
  orderedIds.forEach((id, idx) => {
    const item = cardsState.items.find(
      it => it.id === id && it.questionnaire_slug === questionnaireSlug,
    );
    if (item) item.position = (idx + 1) * 10;
  });
  return prev;
}

// Revert a reorderItemsLocally call. Pass the snapshot returned by that
// function; positions are restored row-by-row. Used by the parent's
// optimistic-with-revert reorder handler.
export function revertItemsOrderLocally(snapshot) {
  if (!snapshot) return;
  for (const [id, position] of snapshot.entries()) {
    const item = cardsState.items.find(it => it.id === id);
    if (item) item.position = position;
  }
}

export function cardsForSection(sectionId) {
  return cardsState.cards
    .filter(c => c.section_id === sectionId)
    .slice()
    .sort((a, b) => a.position - b.position);
}

export function cardCountForSection(sectionId) {
  let n = 0;
  for (const c of cardsState.cards) if (c.section_id === sectionId) n++;
  return n;
}

// Display helper: pick DE first, fall back to EN, then any other key, then
// the slug. Mirrors the project-wide JSONB fallback chain (DE for the SEQ
// admin which works in German; EN as the most likely fallback).
export function pickName(jsonb, fallbackSlug) {
  if (!jsonb || typeof jsonb !== 'object') return fallbackSlug || '';
  if (jsonb.de) return jsonb.de;
  if (jsonb.en) return jsonb.en;
  for (const k of Object.keys(jsonb)) {
    if (jsonb[k]) return jsonb[k];
  }
  return fallbackSlug || '';
}

// Card title preview: prefer the title in DE, fall back to the prompt in
// DE truncated to 60 chars. Phase 2 will surface multilingual previews.
export function pickCardPreview(card) {
  const c = card.content || {};
  if (c.title && (c.title.de || c.title.en)) {
    return c.title.de || c.title.en;
  }
  if (c.headline && (c.headline.de || c.headline.en)) {
    return c.headline.de || c.headline.en;
  }
  const prompt = c.prompt && (c.prompt.de || c.prompt.en);
  if (prompt) {
    return prompt.length > 60 ? prompt.slice(0, 60) + '…' : prompt;
  }
  return '';
}

// Junction-aware version: resolves a display title for a junction item
// based on its item_type. Walks the inlined source row carried under
// `item.item`. Same DE-then-EN-then-any fallback chain as pickCardPreview.
//   - 'question'      → item.item.content.title (i18n object)
//   - 'content_card'  → blocks.find(kind='TITLE').text (i18n object)
//   - 'flow_card'     → item.item.name (i18n object)
export function pickItemPreview(item) {
  if (!item || !item.item) return '';
  const inner = item.item;

  if (item.item_type === 'question') {
    const title = inner.content && inner.content.title;
    if (title && (title.de || title.en)) return title.de || title.en;
    if (title && typeof title === 'object') {
      for (const k of Object.keys(title)) if (title[k]) return title[k];
    }
    return '';
  }

  if (item.item_type === 'content_card') {
    const blocks = Array.isArray(inner.blocks) ? inner.blocks : [];
    const titleBlock = blocks.find(b => b && b.kind === 'TITLE');
    const text = titleBlock && titleBlock.text;
    if (text && (text.de || text.en)) return text.de || text.en;
    if (text && typeof text === 'object') {
      for (const k of Object.keys(text)) if (text[k]) return text[k];
    }
    return '';
  }

  if (item.item_type === 'flow_card') {
    const name = inner.name;
    if (name && (name.de || name.en)) return name.de || name.en;
    if (name && typeof name === 'object') {
      for (const k of Object.keys(name)) if (name[k]) return name[k];
    }
    return '';
  }

  return '';
}

// Auto-suggest the next card code. For question-type cards, scan all
// question cards in the questionnaire for the highest Q<N> and return
// Q<N+1>. For non-question types, return an empty string — the user types
// their own code (e.g. FRUITS_COMPLETE).
export function suggestCardCode(questionnaireSlug, cardType) {
  if (cardType !== 'question') return '';
  let maxN = 0;
  for (const c of cardsState.cards) {
    if (c.questionnaire_slug !== questionnaireSlug) continue;
    if (c.card_type !== 'question') continue;
    const m = /^Q(\d+)$/.exec(c.code);
    if (m) {
      const n = parseInt(m[1], 10);
      if (n > maxN) maxN = n;
    }
  }
  return `Q${maxN + 1}`;
}

// Auto-suggest a section slug from its DE name. Lowercase, replace umlauts
// and spaces, trim to the slug constraint. Editable in the form, just a
// starting point.
export function suggestSectionSlug(name) {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

// Slug constraint mirrors the schema CHECK (seq_card_sections_slug_format).
export const SLUG_REGEX = /^[a-z][a-z0-9_-]{1,63}$/;

// Code constraint mirrors the schema CHECK (seq_cards_code_format).
export const CODE_REGEX = /^[A-Z][A-Z0-9_]{0,31}$/;

// Allowed values for the `input_style` field on question-type cards.
// Stored inside the card's content JSONB as content.input_style. Only
// applies to card_type === 'question'; section_complete and info cards
// don't use it. Phase 2 will expose an editor; Phase 1 sets it once on
// creation via the inline mini-form.
export const INPUT_STYLES = ['frequency-4', 'frequency-list', 'stepper', 'long-list', 'scale'];

// ---------- composition counts ----------

// Per-questionnaire counts of each card type plus the section count. Used
// by the top-level summary strip in QuestionnaireSectionList. Always
// returns all four keys (zero for missing types) so the renderer can show
// "0 info cards" without conditional logic.
export function cardTypeCountsForQuestionnaire(questionnaireSlug) {
  const counts = { question: 0, section_complete: 0, info: 0, sections: 0 };
  for (const c of cardsState.cards) {
    if (c.questionnaire_slug !== questionnaireSlug) continue;
    if (counts[c.card_type] !== undefined) counts[c.card_type]++;
  }
  for (const s of cardsState.sections) {
    if (s.questionnaire_slug === questionnaireSlug) counts.sections++;
  }
  return counts;
}

// Per-section counts. Used by the section header summary line, which
// only renders the types that are present, so callers filter out zeros.
export function cardTypeCountsInSection(sectionId) {
  const counts = { question: 0, section_complete: 0, info: 0 };
  for (const c of cardsState.cards) {
    if (c.section_id !== sectionId) continue;
    if (counts[c.card_type] !== undefined) counts[c.card_type]++;
  }
  return counts;
}

// ---------- mutators ----------

// Replace a section's multilingual name in the local store. Used by
// the section editor modal — Phase 2 still local-only; the future
// PATCH /api/seq/sections/:id endpoint will also send the new name and
// return the updated row, at which point this helper can take the
// server response and splice it in instead.
export function updateSectionName(sectionId, name) {
  const section = cardsState.sections.find(s => s.id === sectionId);
  if (!section) return null;
  section.name = name && typeof name === 'object' ? { ...name } : {};
  return section;
}

export function removeSection(sectionId) {
  const idx = cardsState.sections.findIndex(s => s.id === sectionId);
  if (idx === -1) return false;
  cardsState.sections.splice(idx, 1);
  return true;
}

export function removeCard(cardId) {
  const idx = cardsState.cards.findIndex(c => c.id === cardId);
  if (idx === -1) return false;
  cardsState.cards.splice(idx, 1);
  return true;
}

// Apply a new ordering, given a list of section ids in the desired order.
// Re-numbers their .position fields to match. Sections not in the list are
// left untouched.
export function reorderSections(orderedIds) {
  orderedIds.forEach((id, idx) => {
    const s = cardsState.sections.find(x => x.id === id);
    if (s) s.position = idx + 1;
  });
}

// Same idea for cards within a section.
export function reorderCardsInSection(sectionId, orderedIds) {
  orderedIds.forEach((id, idx) => {
    const c = cardsState.cards.find(x => x.id === id);
    if (c && c.section_id === sectionId) c.position = idx + 1;
  });
}

// True if a slug already exists under the given questionnaire (case-sensitive,
// matches DB unique index seq_card_sections_qid_slug_unique).
export function isSectionSlugTaken(questionnaireSlug, slug, exceptId = null) {
  return cardsState.sections.some(s =>
    s.questionnaire_slug === questionnaireSlug
    && s.slug === slug
    && s.id !== exceptId
  );
}

// True if a card code already exists under the given questionnaire.
export function isCardCodeTaken(questionnaireSlug, code, exceptId = null) {
  return cardsState.cards.some(c =>
    c.questionnaire_slug === questionnaireSlug
    && c.code === code
    && c.id !== exceptId
  );
}

// ---------- translation export / import ----------
//
// Phase 1: download = build a JSON export from the local store; upload =
// parse the JSON, walk it against the local store, and either preview the
// would-be changes or apply them.
//
// Future endpoint shapes (Phase 2):
//   GET  /api/seq/questionnaires/:slug/translations  → returns the export object
//   POST /api/seq/questionnaires/:slug/translations  → body: parsed file;
//                                                      response: { sections_updated, cards_updated, fields_changed }

export const SUPPORTED_LANGS = ['de', 'en', 'fr', 'it'];

// True if every key on `obj` is one of the four supported language codes,
// and there is at least one such key. Used to recognise a JSONB
// translation slot (vs a generic content blob like
// {subtype: 'tip', body: {...}}).
function isLangMap(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  const keys = Object.keys(obj);
  if (keys.length === 0) return false;
  return keys.every(k => SUPPORTED_LANGS.includes(k));
}

// Return a copy of a lang map with all four supported langs present.
// Missing langs become null. Used at export time so the file always shows
// the full surface area.
function normaliseLangMap(obj) {
  const out = {};
  for (const lang of SUPPORTED_LANGS) {
    const v = obj && Object.prototype.hasOwnProperty.call(obj, lang) ? obj[lang] : null;
    out[lang] = (typeof v === 'string' && v.length > 0) ? v : null;
  }
  return out;
}

// Recursively normalise an object: any subtree that looks like a lang map
// is filled in to all four langs; everything else is preserved as-is
// (objects recursed into, arrays mapped, scalars passed through).
function normaliseTranslations(value) {
  if (isLangMap(value)) return normaliseLangMap(value);
  if (Array.isArray(value)) return value.map(normaliseTranslations);
  if (value && typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) out[k] = normaliseTranslations(value[k]);
    return out;
  }
  return value;
}

export function buildTranslationExport(questionnaireSlug, questionnaireName) {
  // Junction Step 3: walk the new items collection instead of the
  // legacy seq_cards rows (which are dormant post-Step-2 and would
  // produce an empty export). Export FORMAT is unchanged — same
  // {sections, cards} shape — but each card's content is now drawn
  // from the inlined source row under item.item, varying by item_type:
  //
  //   question      → item.item.content (i18n title/prompt/etc.)
  //   content_card  → item.item.blocks (array of {kind, text} blocks)
  //   flow_card     → item.item.name (i18n object)
  //
  // The export's `code` becomes the source row's code where present
  // (questions and flow cards have one; content cards don't, so we
  // fall back to a "cc_<id>" key). `card_type` carries the new
  // item_type value verbatim so the upload path can dispatch the
  // same way the renderer does.

  const sections = sectionsForQuestionnaire(questionnaireSlug).map(s => ({
    slug: s.slug,
    name: normaliseLangMap(s.name),
  }));

  const sectionSlugById = {};
  const sectionPosBySlug = {};
  for (const s of sectionsForQuestionnaire(questionnaireSlug)) {
    sectionSlugById[s.id] = s.slug;
    sectionPosBySlug[s.slug] = s.position;
  }

  const items = cardsState.items
    .filter(it => it.questionnaire_slug === questionnaireSlug)
    .slice()
    .sort((a, b) => {
      const aSlug = sectionSlugById[a.section_id];
      const bSlug = sectionSlugById[b.section_id];
      const aSec = aSlug ? sectionPosBySlug[aSlug] : 9999;
      const bSec = bSlug ? sectionPosBySlug[bSlug] : 9999;
      if (aSec !== bSec) return aSec - bSec;
      return a.position - b.position;
    })
    .map(it => {
      const src = it.item || {};
      const code = src.code || (it.item_type === 'content_card' ? `cc_${src.id}` : `${it.item_type}_${src.id}`);
      return {
        code,
        card_type: it.item_type,
        section_slug: sectionSlugById[it.section_id] || null,
        content: normaliseTranslations(src),
      };
    });

  return {
    questionnaire: questionnaireSlug,
    questionnaire_name: questionnaireName || '',
    exported_at: new Date().toISOString(),
    sections,
    cards: items,
  };
}

// ── Spec-format questionnaire export ────────────────────────────────
// See docs/SPEC_questionnaire_json_export.md. A read-only, deterministic
// JSON export of one questionnaire: sections in authored order, items in
// order within each section, every authored i18n field expanded to all
// four locales (missing → null) so it can be handed to an LLM to fill the
// gaps, or diffed against another export.
//
// Stable ids ride along on every section, item, and — inside question
// content — every option / matrix row / scale option, so a future import
// can map translations back by id rather than by matching text.
//
// Flow-card text is intentionally NOT pulled in: it lives in the app's
// i18n bundle, translated like any other interface string, not in the
// questionnaire data. Flow cards export as a bare {code} reference so the
// structure stays complete for comparison without reaching across systems.
//
// Section-less "intro slot" items (section_id null) are not exported —
// this mirrors the editor, which groups strictly by section.

const EXPORT_SCHEMA_VERSION = 1;

function buildExportItem(it, order) {
  const src = it.item || {};
  const base = { id: it.id, order, item_type: it.item_type };

  if (it.item_type === 'question') {
    return {
      ...base,
      code: src.code || null,
      question_type: src.question_type || null,
      // Full content tree, recursively normalised: title, subtitle,
      // prompt, tooltips, unit_label, and options/scale/items (with their
      // stable ids) are all preserved. Non-text scalars (ids, scores,
      // min/max) pass through untouched.
      content: normaliseTranslations(src.content || {}),
      help_text: normaliseLangMap(src.help_text || {}),
    };
  }

  if (it.item_type === 'content_card') {
    const fields = {};
    for (const b of (src.blocks || [])) {
      if (!b || typeof b.kind !== 'string') continue;
      if (b.kind.startsWith('IMAGE')) continue; // images carry no translatable text
      if (b.text && typeof b.text === 'object') {
        fields[b.kind.toLowerCase()] = normaliseLangMap(b.text);
      }
    }
    return { ...base, sub_type: src.type || null, fields };
  }

  if (it.item_type === 'flow_card') {
    // Bare reference only — text lives in the i18n bundle.
    return { ...base, code: src.code || null, text_source: 'i18n_bundle' };
  }

  return base;
}

export function buildQuestionnaireExport(questionnaireSlug, questionnaireName) {
  // Resolve sections + items the same way the editor (sortedSections) and
  // the runtime do: straight from the resolved items list. Each item
  // carries its section id/name/slug inline (the /items endpoint joins
  // seq_card_sections). We deliberately do NOT read cardsState.sections —
  // that separate store isn't populated on the editor page, which is what
  // produced the empty `sections: []` export. Sections appear in the order
  // their first item arrives (the order the items query returns them);
  // items follow their stored position within each section.
  const sectionOrder = [];        // section ids, first-appearance order
  const sectionMeta = new Map();  // section id → { name, slug }
  const itemsBySection = new Map();

  for (const it of cardsState.items) {
    if (it.questionnaire_slug !== questionnaireSlug) continue;
    if (it.section_id == null) continue; // intro-slot items aren't section-grouped
    if (!itemsBySection.has(it.section_id)) {
      itemsBySection.set(it.section_id, []);
      sectionMeta.set(it.section_id, {
        name: it.section_name || {},
        slug: it.section_slug || null,
      });
      sectionOrder.push(it.section_id);
    }
    itemsBySection.get(it.section_id).push(it);
  }
  for (const arr of itemsBySection.values()) {
    arr.sort((a, b) => (a.position || 0) - (b.position || 0));
  }

  const sections = sectionOrder.map((sid, sIdx) => {
    const meta = sectionMeta.get(sid);
    return {
      id: sid,
      order: sIdx + 1,
      slug: meta.slug,
      name: normaliseLangMap(meta.name || {}),
      items: itemsBySection.get(sid).map((it, iIdx) => buildExportItem(it, iIdx + 1)),
    };
  });

  return {
    export: {
      questionnaire_id: questionnaireSlug,
      name: isLangMap(questionnaireName)
        ? normaliseLangMap(questionnaireName)
        : (questionnaireName || ''),
      exported_at: new Date().toISOString(),
      schema_version: EXPORT_SCHEMA_VERSION,
    },
    sections,
  };
}

// Count how many lang slots in the export are filled (non-null string),
// per language. Used in the upload preview summary.
export function countFilledLangs(fileData) {
  const counts = { de: 0, en: 0, fr: 0, it: 0 };
  function walk(value) {
    if (isLangMap(value)) {
      for (const lang of SUPPORTED_LANGS) {
        const v = value[lang];
        if (typeof v === 'string' && v.length > 0) counts[lang]++;
      }
      return;
    }
    if (Array.isArray(value)) { value.forEach(walk); return; }
    if (value && typeof value === 'object') {
      for (const k of Object.keys(value)) walk(value[k]);
    }
  }
  for (const s of (fileData.sections || [])) walk(s.name);
  for (const c of (fileData.cards || [])) walk(c.content);
  return counts;
}

// Walk an "incoming" subtree (from the uploaded file) against an
// "existing" subtree (from the local store). For each lang slot in the
// incoming side that differs from the existing side, record a change and
// — if commit is true — apply the new value. Non-lang-map scalars are
// never touched (codes, slugs, positions, types are all out of scope).
function walkAndCollect(existing, incoming, pathParts, changes, commit) {
  if (incoming == null) return;
  if (isLangMap(incoming)) {
    if (!existing || typeof existing !== 'object' || Array.isArray(existing)) return;
    for (const lang of SUPPORTED_LANGS) {
      const newVal = incoming[lang];
      if (newVal == null || typeof newVal !== 'string') continue;
      const oldVal = existing[lang];
      if (oldVal === newVal) continue;
      changes.push({
        path: pathParts.join('.'),
        lang,
        action: oldVal == null || oldVal === '' ? 'added' : 'changed',
        oldValue: oldVal == null ? null : oldVal,
        newValue: newVal,
      });
      if (commit) existing[lang] = newVal;
    }
    return;
  }
  if (Array.isArray(incoming)) {
    if (!Array.isArray(existing)) return;
    const len = Math.min(incoming.length, existing.length);
    for (let i = 0; i < len; i++) {
      walkAndCollect(existing[i], incoming[i], [...pathParts, `[${i}]`], changes, commit);
    }
    return;
  }
  if (typeof incoming === 'object') {
    if (!existing || typeof existing !== 'object' || Array.isArray(existing)) return;
    for (const k of Object.keys(incoming)) {
      walkAndCollect(existing[k], incoming[k], [...pathParts, k], changes, commit);
    }
  }
  // Scalar — never updated.
}

// Either preview or apply a parsed translation file against the local
// store. Returns counts and an itemised change list; mutates cardsState
// only when commit is true.
function processTranslationFile(questionnaireSlug, fileData, commit) {
  const result = {
    sections_count: (fileData.sections || []).length,
    cards_count: (fileData.cards || []).length,
    language_counts: countFilledLangs(fileData),
    changes: [],            // [{ scope, identifier, path, lang, action, oldValue, newValue }]
    sections_updated: 0,
    cards_updated: 0,
    skipped_sections: [],   // section slugs in the file that are not in the store
    skipped_cards: [],      // card codes in the file that are not in the store
  };

  // Sections: match by slug.
  for (const fileSec of (fileData.sections || [])) {
    if (!fileSec || typeof fileSec.slug !== 'string') continue;
    const local = cardsState.sections.find(s =>
      s.questionnaire_slug === questionnaireSlug && s.slug === fileSec.slug
    );
    if (!local) {
      result.skipped_sections.push(fileSec.slug);
      continue;
    }
    const before = result.changes.length;
    // Ensure local.name is an object so the walker has somewhere to write.
    if (!local.name || typeof local.name !== 'object') local.name = {};
    walkAndCollect(local.name, fileSec.name, ['name'], result.changes, commit);
    // Tag the latest changes with scope/identifier for nicer rendering.
    for (let i = before; i < result.changes.length; i++) {
      result.changes[i].scope = 'section';
      result.changes[i].identifier = local.slug;
    }
    if (result.changes.length > before) result.sections_updated++;
  }

  // Cards: match by code (unique per questionnaire per the schema).
  for (const fileCard of (fileData.cards || [])) {
    if (!fileCard || typeof fileCard.code !== 'string') continue;
    const local = cardsState.cards.find(c =>
      c.questionnaire_slug === questionnaireSlug && c.code === fileCard.code
    );
    if (!local) {
      result.skipped_cards.push(fileCard.code);
      continue;
    }
    const before = result.changes.length;
    if (!local.content || typeof local.content !== 'object') local.content = {};
    walkAndCollect(local.content, fileCard.content, [], result.changes, commit);
    for (let i = before; i < result.changes.length; i++) {
      result.changes[i].scope = 'card';
      result.changes[i].identifier = local.code;
    }
    if (result.changes.length > before) result.cards_updated++;
  }

  return result;
}

export function previewTranslationApply(questionnaireSlug, fileData) {
  return processTranslationFile(questionnaireSlug, fileData, /* commit */ false);
}

export function applyTranslationData(questionnaireSlug, fileData) {
  return processTranslationFile(questionnaireSlug, fileData, /* commit */ true);
}
